import React from 'react'

import { Box, Button, List, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  CustomCategoriesPageDocument,
  DisableFinanceCustomCategoryDocument,
  EnableFinanceCustomCategoryDocument,
  FinanceCustomCategoryItemFragment,
  FinanceCustomCategoryLegalEntityFragment,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import CreateCustomCategoryModal from '../CreateCustomCategoryModal'
import EditCustomCategoryModal from '../EditCustomCategoryModal'
import SubcategoryItem from './SubcategoryItem'

interface Props {
  categories: FinanceCustomCategoryItemFragment[]
  legalEntities: FinanceCustomCategoryLegalEntityFragment[]
  customCategory: FinanceCustomCategoryItemFragment
  canEditLedgerAccountAssignments: boolean
}

const CategoryBlock = ({
  categories,
  legalEntities,
  customCategory,
  canEditLedgerAccountAssignments,
}: Props) => {
  const [createSubcategoryModalOpen, setCreateSubcategoryModalOpen] = React.useState(false)
  const [editCategoryModalOpen, setEditCategoryModalOpen] = React.useState(false)

  const { company } = useCurrentSession()

  const [enableCategory] = useMutation(EnableFinanceCustomCategoryDocument, {
    extractorFn: (data) => data.financeMutations.enableFinanceCustomCategory,
  })

  const [disableCategory] = useMutation(DisableFinanceCustomCategoryDocument, {
    extractorFn: (data) => data.financeMutations.disableFinanceCustomCategory,
  })

  const onToggleCategory = async () => {
    let handled

    if (customCategory.enabled) {
      const result = await disableCategory({
        variables: {
          id: customCategory.id,
          companyId: company.id,
        },
        refetchQueries: [CustomCategoriesPageDocument],
      }).then((res) => {
        track(trackingEvents.accounting.settings.categories.disableCategory)
        return res
      })

      handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      })
    } else {
      const result = await enableCategory({
        variables: {
          id: customCategory.id,
          companyId: company.id,
        },
        refetchQueries: [CustomCategoriesPageDocument],
      }).then((res) => {
        track(trackingEvents.accounting.settings.categories.enableCategory)
        return res
      })

      handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      })
    }

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    return handled
  }

  return (
    <>
      <List.Item key={customCategory.id}>
        <List.Title>
          <Text.Normal color="grey1000" weight="semibold">
            {customCategory.label}
          </Text.Normal>
        </List.Title>
        <List.ActionItem>
          <Box flexDirection="row" gap="s8">
            <TogglerCheckbox
              onChange={onToggleCategory}
              value={customCategory.enabled}
              disabled={customCategory.protected}
              aria-label={i18n.t('finance.settings.accounting.custom_categories.actions.toggle')}
            />
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center">
                <PopoverMenu.TouchableOption
                  title={i18n.t('finance.settings.accounting.custom_categories.actions.add')}
                  icon="add"
                  onClick={() => setCreateSubcategoryModalOpen(true)}
                />
                <PopoverMenu.TouchableOption
                  title={i18n.t('finance.settings.accounting.custom_categories.actions.edit')}
                  icon="pencil"
                  onClick={() => {
                    setEditCategoryModalOpen(true)
                  }}
                />
              </PopoverMenu.Content>
            </PopoverMenu>
          </Box>
        </List.ActionItem>
      </List.Item>
      {customCategory.subcategories &&
        customCategory.subcategories.map((subcategory) => (
          <SubcategoryItem
            key={subcategory.id}
            subcategory={subcategory}
            categories={categories}
            legalEntities={legalEntities}
            canEditLedgerAccountAssignments={canEditLedgerAccountAssignments}
            isParentEnabled={customCategory.enabled}
          />
        ))}
      {createSubcategoryModalOpen && (
        <CreateCustomCategoryModal
          parentCategoryId={customCategory.id}
          parentCategories={categories}
          legalEntities={legalEntities}
          canEditLedgerAccountAssignments={canEditLedgerAccountAssignments}
          onClose={() => setCreateSubcategoryModalOpen(false)}
        />
      )}
      {editCategoryModalOpen && (
        <EditCustomCategoryModal
          category={customCategory}
          legalEntities={legalEntities}
          canEditLedgerAccountAssignments={canEditLedgerAccountAssignments}
          onClose={() => setEditCategoryModalOpen(false)}
        />
      )}
    </>
  )
}

export default CategoryBlock
